import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { wellFlowingTemperatureForMD } from "@/libs/calculations/pressureDropCorrelations";
import {
  buildIntervalCasingGeometry,
  calculateMotorAndSealsCasingFriction
} from "../../casingFrictionCalculations";
import type { FluidPropertiesPipelineInputs } from "../types";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";

// Motor -> seals.
export const calculateMotorToSeals = (
  inputs: FluidPropertiesPipelineInputs,
  prev: {
    fluidPropertiesAtMotorContext: FluidPropertiesContext;
    getPressureAtReferenceTVDPSIA: (referenceTVDFt: number) => number;
    casingFrictionLossPSIA: number;
  }
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    includeCasingFrictionLoss,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    bottomOfSealsMDFt,
    bottomOfSealsTVDFt,
    sealsCrossSectionFt2,
    primarySealOuterDiameterInches,
    bottomOfMotorMDFt,
    bottomOfMotorTVDFt,
    effectiveCasingInteriorDiameterAtMotorIn,
    effectiveCasingInteriorDiameterAtSealsIn,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  } = inputs;
  const {
    fluidPropertiesAtMotorContext,
    getPressureAtReferenceTVDPSIA,
    casingFrictionLossPSIA: startingCasingFrictionLossPSIA
  } = prev;
  let casingFrictionLossPSIA = startingCasingFrictionLossPSIA;
  let motorAndSealsCasingFrictionPSIA = 0;

  // P_wf (seals)
  const motorAndSealsPressureProperties = calculateMotorAndSealsCasingFriction({
    correlations,
    bottomOfMotorMDFt,
    bottomOfMotorTVDFt,
    bottomOfSealsMDFt,
    bottomOfSealsTVDFt,
    entryRows,
    fluidPropertiesAtMotorContext,
    surfaceTemperatureF: basicData.surfaceTemperatureF,
    bottomholeTemperatureF: basicData.bottomholeTemperatureF,
    casingGeometry: buildIntervalCasingGeometry(
      bottomOfMotorMDFt,
      bottomOfSealsMDFt,
      topCasingInteriorDiameterIn,
      topCasingLengthFt,
      bottomCasingInteriorDiameterIn,
      bottomCasingLengthFt,
      effectiveCasingInteriorDiameterAtMotorIn,
      primarySealOuterDiameterInches
    ),
    bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
    producingBottomholePressurePSIA: basicData.producingBottomholePressurePSIA
  });

  motorAndSealsCasingFrictionPSIA =
    motorAndSealsPressureProperties.frictionPressurePSIA;
  let motorAndSealsAppliedFrictionPSIA = 0;
  if (includeCasingFrictionLoss) {
    motorAndSealsAppliedFrictionPSIA = motorAndSealsCasingFrictionPSIA;
    casingFrictionLossPSIA += motorAndSealsAppliedFrictionPSIA;
  }
  const wellFlowingPressureAtSealPSIA =
    getPressureAtReferenceTVDPSIA(bottomOfSealsTVDFt) -
    motorAndSealsAppliedFrictionPSIA;

  const downholeSealTemperatureF = wellFlowingTemperatureForMD(
    bottomOfSealsMDFt,
    basicData.datumPointFt,
    basicData.bottomholeTemperatureF,
    basicData.surfaceTemperatureF
  );

  const fluidPropertiesAtSealsContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtSealPSIA,
    downholeSealTemperatureF,
    effectiveCasingInteriorDiameterAtSealsIn,
    desiredFlowRates,
    fluidPropertiesAtMotorContext.flowRatesInSitu,
    fluidPropertiesAtMotorContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    sealsCrossSectionFt2,
    0,
    fluidPropertiesOverrides,
    condensateApi
  );
  return {
    motorAndSealsCasingFrictionPSIA,
    wellFlowingPressureAtSealPSIA,
    downholeSealTemperatureF,
    fluidPropertiesAtSealsContext,
    casingFrictionLossPSIA
  };
};
